// ─────────────────────────────────────────────────────────────────────
// IS THE SERVER ANSWERING RIGHT NOW? (#537)
//
// THE PROBLEM THIS EXISTS FOR, verbatim from the person it happened to:
// «Now you can be creating some tournament and suddenly buttons stop working.
// You refresh and your last work is gone.»
//
// When this was written every deploy restarted the API's App Service. Since
// #897 a deploy only re-pins the image and the platform swaps containers, but
// the API can still be unreachable for a while — a swap that goes wrong, a
// platform restart, the shared plan saturated. And the client is storage-first:
// a write AWAITS the server before state moves. So while the server is away
// the awaits fail, the handlers bail, and the screen shows nothing at all. The button is not broken; it is waiting on
// a machine that is coming back. Nobody could tell, so the reasonable thing
// to do was refresh, which is the one action that loses the work.
//
// WHY THE SIGNAL LIVES AT THE TRANSPORT. `apiFetch` is the single point every
// call passes through. A banner driven from one screen's error state would be
// right on that screen and absent on the other twenty; driven from here it is
// right everywhere, including calls made by code written after this.
//
// TRANSPORT FAILURES ONLY. A 4xx is an ANSWER — the server is up and said no,
// and «the server is not responding» over a refusal is a lie that sends
// somebody to wait for a recovery that already happened. Only a thrown fetch
// (no answer at all) and the gateway codes App Service returns while it is
// coming back count.
// ─────────────────────────────────────────────────────────────────────

export type Reachability = 'ok' | 'unreachable';

/**
 * TWO consecutive failures, not one.
 *
 * `saveWithRetry` (#276) already swallows a blip that clears in a few hundred
 * milliseconds, and the banner should keep the same promise: a hiccup nobody
 * would have noticed must not produce a warning nobody needs. A real restart
 * fails every call for many seconds, so two is reached immediately.
 */
const FAILURES_BEFORE_UNREACHABLE = 2;

/** Gateway codes an App Service returns while it is restarting. */
const RESTARTING = new Set([502, 503, 504]);

let consecutiveFailures = 0;
let state: Reachability = 'ok';
const listeners = new Set<(r: Reachability) => void>();

function publish(next: Reachability) {
  if (next === state) return;
  state = next;
  for (const l of listeners) l(state);
}

/** A response arrived. Anything the server actually answered means it is up. */
export function reportServerAnswered(status: number): void {
  if (RESTARTING.has(status)) {
    reportServerSilent();
    return;
  }
  consecutiveFailures = 0;
  publish('ok');
}

/** No answer at all — a thrown fetch, or a deadline nothing came back from. */
export function reportServerSilent(): void {
  consecutiveFailures += 1;
  if (consecutiveFailures >= FAILURES_BEFORE_UNREACHABLE) publish('unreachable');
}

export const serverReachability = (): Reachability => state;

export function subscribeReachability(fn: (r: Reachability) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Tests only — the module is a singleton by design, like the session. */
export function resetReachability(): void {
  consecutiveFailures = 0;
  state = 'ok';
  listeners.clear();
}
